import { Component, signal, inject, computed, viewChild, ElementRef, afterNextRender } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { finalize, startWith, tap } from 'rxjs';
import { PricePolicyType, PricePolicy } from '../../interfaces/price-policy';
import { CreatePricePolicyRequest, UpdatePricePolicyRequest, PricePolicyV2Service } from 'src/app/shared/services/price-policy-v2.service';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { NoteCreationModalComponent } from 'src/app/shared/components/modals/price-policy-note-creation-modal/note-creation-modal.component';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdmPriceExInclVatComponent } from 'src/app/features/prices/price-policies/components/price-ex-incl-vat/price-ex-incl-vat.component';
import { AdmPriceFeedbackComponent } from '../price-feedback/adm-price-feedback.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Organization } from 'src/app/features/organization/organization-models';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { AdminEnumSelectListComponent } from 'src/app/shared/components/admin-enum-select-list/admin-enum-select-list.component';
import { TranslatePricePolicyTypePipe } from 'src/app/shared/pipes/translate-price-policy-type';
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { FEATURE_FLAGS } from 'src/app/shared/services/feature-flags.const';
import { AdmPricePolicyFallbackComponent, PricePolicyFallbackConfig } from '../price-policy-fallback/price-policy-fallback.component';
import { getPricePolicyErrorMessage } from '../../helpers/price-policy-error.helper';
import { DkCurrencyPipe } from 'src/app/shared/pipes/dk-currency';
import { IPricePolicyCreateOrEditForm, OrganizationOption } from './price-policies-create-or-edit-dialog.interface';
import { PriceFeedbackConfig } from '../price-feedback/adm-price-feedback.interface';

@Component({
  selector: 'adm-price-policies-create-or-edit-dialog',
  templateUrl: './price-policies-create-or-edit-dialog.component.html',
  styleUrls: ['./price-policies-create-or-edit-dialog.component.scss'],
  imports: [
    AdmOrgTypeaheadComponent,
    AdmPriceExInclVatComponent,
    AdmPriceFeedbackComponent,
    FeedbackInfoComponent,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatRadioButton,
    MatRadioGroup,
    AdminEnumSelectListComponent,
    AdmPricePolicyFallbackComponent,
    DkCurrencyPipe,
  ],
})
export class PricePoliciesCreateOrEditDialogComponent {
  private pricePolicyService = inject(PricePolicyV2Service);
  private dialog = inject(MatDialog);
  private snackbarService = inject(SnackbarService);
  private nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');
  private featureFlagService = inject(FeatureFlagService);

  public dialogRef = inject<MatDialogRef<PricePoliciesCreateOrEditDialogComponent>>(MatDialogRef);
  public data = inject<{ pricePolicy: PricePolicy | null }>(MAT_DIALOG_DATA);
  public editMode: boolean = this.data.pricePolicy != null; // Use loose equality to catch both null and undefined
  public pricePolicyCreateFeatureFlagEnabled = signal<boolean>(false);

  public saving = signal<boolean>(false);
  public typeResetTrigger = signal<number>(0);

  // Enum values as array for dropdown options
  public typeOptions = computed(() =>
    !this.pricePolicyCreateFeatureFlagEnabled()
      ? [PricePolicyType.FixedWithOkMeter, PricePolicyType.FixedWithoutOkMeter]
      : Object.values(PricePolicyType)
  );
  public translatePricePolicyTypePipe = new TranslatePricePolicyTypePipe();

  public readonly initialType = this.data.pricePolicy?.type ?? PricePolicyType.FixedWithoutOkMeter;
  public readonly initialBasePriceExclVat = this.data.pricePolicy?.basePriceInKrWithoutVat?.fixed?.value ?? null;
  public readonly initialBasePriceLow = this.data.pricePolicy?.basePriceInKrWithoutVat?.dynamic?.low ?? null;
  public readonly initialBasePriceHigh = this.data.pricePolicy?.basePriceInKrWithoutVat?.dynamic?.high ?? null;
  public readonly initialOkSupplementExclVat = this.data.pricePolicy?.okSupplementInKrWithoutVat ?? null;
  public readonly initialCustomerSupplementExclVat = this.data.pricePolicy?.organizationSupplementInKrWithoutVat ?? null;

  public pricePolicyFormGroup = new FormGroup<IPricePolicyCreateOrEditForm>({
    name: new FormControl<string>(this.data.pricePolicy?.name ?? '', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern(/.*\S.*/)],
    }),
    isGlobal: new FormControl<boolean>(!this.data.pricePolicy?.organizationId, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    affiliation: new FormControl<boolean>(
      this.data.pricePolicy == null
        ? true // Default to Global (true) in create mode
        : !this.data.pricePolicy.organizationId, // 0 or null both mean "no organization" (Global)
      { nonNullable: true, validators: [Validators.required] }
    ),

    organization: new FormControl<OrganizationOption | null>(
      this.data.pricePolicy?.organizationId
        ? { id: this.data.pricePolicy.organizationId, name: this.data.pricePolicy.organizationName ?? '' }
        : null
    ),
    type: new FormControl<PricePolicyType>(this.data.pricePolicy?.type ?? PricePolicyType.FixedWithoutOkMeter, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    fallbackPricePolicy: new FormControl<number | null>(this.data.pricePolicy?.fallbackPricePolicyId ?? null),
    // Fixed type field
    pricePerUnit: new FormControl<number | null>(this.data.pricePolicy?.basePriceInKrWithoutVat?.fixed?.value ?? null, {
      validators: [Validators.required, Validators.max(99.99), Validators.min(0)],
    }),
    // Dynamic type fields
    lowLimit: new FormControl<number | null>(this.data.pricePolicy?.basePriceInKrWithoutVat?.dynamic?.low ?? null),
    highLimit: new FormControl<number | null>(this.data.pricePolicy?.basePriceInKrWithoutVat?.dynamic?.high ?? null),
    // Supplement fields
    okSupplement: new FormControl<number | null>(this.data.pricePolicy?.okSupplementInKrWithoutVat ?? null),
    organizationSupplement: new FormControl<number | null>(this.data.pricePolicy?.organizationSupplementInKrWithoutVat ?? null),
  });

  public resetOrgTrigger = signal<{ reset: boolean }>({ reset: false });

  // Convert affiliation control value to signal
  public affiliationValue = toSignal(
    this.pricePolicyFormGroup.controls.affiliation.valueChanges.pipe(startWith(this.pricePolicyFormGroup.controls.affiliation.value))
  );

  // Computed signals based on affiliation value
  public isGlobalSelected = computed(() => this.affiliationValue() === true);

  // Convert type control value to signal
  public typeValue = toSignal(
    this.pricePolicyFormGroup.controls.type.valueChanges.pipe(startWith(this.pricePolicyFormGroup.controls.type.value))
  );

  // Convert form values to signals for price feedback component
  public basePriceLow = toSignal(
    this.pricePolicyFormGroup.controls.lowLimit.valueChanges.pipe(startWith(this.pricePolicyFormGroup.controls.lowLimit.value))
  );
  public basePriceHigh = toSignal(
    this.pricePolicyFormGroup.controls.highLimit.valueChanges.pipe(startWith(this.pricePolicyFormGroup.controls.highLimit.value))
  );
  public basePriceFixed = toSignal(
    this.pricePolicyFormGroup.controls.pricePerUnit.valueChanges.pipe(startWith(this.pricePolicyFormGroup.controls.pricePerUnit.value))
  );
  public okSupplementValue = toSignal(
    this.pricePolicyFormGroup.controls.okSupplement.valueChanges.pipe(startWith(this.pricePolicyFormGroup.controls.okSupplement.value))
  );
  public organizationSupplementValue = toSignal(
    this.pricePolicyFormGroup.controls.organizationSupplement.valueChanges.pipe(
      startWith(this.pricePolicyFormGroup.controls.organizationSupplement.value)
    )
  );

  // Organization ID for fallback price policy typeahead
  public organizationIdValue = toSignal(
    this.pricePolicyFormGroup.controls.organization.valueChanges.pipe(startWith(this.pricePolicyFormGroup.controls.organization.value))
  );

  public organizationIdForFallback = computed(() => this.organizationIdValue()?.id ?? null);

  public readonly initialFallbackPricePolicyId = this.data.pricePolicy?.fallbackPricePolicyId ?? null;

  public fallbackConfig = computed<PricePolicyFallbackConfig>(() => ({
    isGlobal: this.isGlobalSelected(),
    organizationId: this.organizationIdForFallback(),
    pricePolicyId: this.initialFallbackPricePolicyId,
    currentPricePolicyId: this.data.pricePolicy?.id ?? null,
  }));

  public priceFeedbackConfig = computed<PriceFeedbackConfig>(() => ({
    basePriceLow: this.basePriceLow() ?? null,
    basePriceHigh: this.basePriceHigh() ?? null,
    basePriceFixed: this.basePriceFixed() ?? null,
    okSupplement: this.okSupplementValue() ?? null,
    organizationSupplement: this.organizationSupplementValue() ?? null,
    type: this.typeValue() ?? PricePolicyType.FixedWithOkMeter,
  }));

  public get pricePerUnit() {
    return this.pricePolicyFormGroup.controls.pricePerUnit;
  }

  public selectOrganization = (org: Organization | null) => {
    const currentOrgId = this.pricePolicyFormGroup.controls.organization.value?.id;
    const newOrgId = org?.id;

    this.pricePolicyFormGroup.patchValue({ organization: org as OrganizationOption | null });

    // Only mark as dirty if the value actually changed
    if (currentOrgId !== newOrgId) {
      this.pricePolicyFormGroup.markAsDirty();
    }
  };

  public typeSelected = (type: PricePolicyType | null) => {
    if (type == null) {
      return;
    }

    const currentType = this.pricePolicyFormGroup.controls.type.value;

    this.pricePolicyFormGroup.patchValue({ type });

    // Only mark as dirty if the value actually changed
    if (currentType !== type) {
      this.pricePolicyFormGroup.markAsDirty();
    }
  };

  public fallbackPricePolicySelected = (pricePolicy: PricePolicy | null) => {
    const currentFallbackId = this.pricePolicyFormGroup.controls.fallbackPricePolicy.value;
    const newFallbackId = pricePolicy?.id ?? null;

    this.pricePolicyFormGroup.patchValue({ fallbackPricePolicy: newFallbackId });

    // Only mark as dirty if the value actually changed
    if (currentFallbackId !== newFallbackId) {
      this.pricePolicyFormGroup.markAsDirty();
    }
  };

  constructor() {
    afterNextRender(() => {
      this.nameInput()?.nativeElement.focus();
    });

    this.featureFlagService
      .getFeatureFlag(FEATURE_FLAGS.PRICE_POLICY_V2_CREATE)
      .pipe(
        tap(flagResult => this.pricePolicyCreateFeatureFlagEnabled.set(flagResult.result)),
        takeUntilDestroyed()
      )
      .subscribe();

    // In edit mode, affiliation and organization are always disabled
    if (this.editMode) {
      this.pricePolicyFormGroup.controls.affiliation.disable();
      this.pricePolicyFormGroup.controls.organization.disable();
    }

    // Initialize organization field validators based on initial affiliation value
    const initialIsGlobal = this.pricePolicyFormGroup.controls.affiliation.value;
    if (!initialIsGlobal) {
      // If Organisation is selected initially, set validator
      this.pricePolicyFormGroup.controls.organization.setValidators(Validators.required);
    }

    // Dynamically set validators based on type selection
    let previousType = this.pricePolicyFormGroup.controls.type.value;
    this.pricePolicyFormGroup.controls.type.valueChanges
      .pipe(
        startWith(this.pricePolicyFormGroup.controls.type.value),
        tap(type => {
          const { pricePerUnit, lowLimit, highLimit, fallbackPricePolicy, okSupplement, organizationSupplement } =
            this.pricePolicyFormGroup.controls;

          // Only reset values when the user actually changes type (not on initial setup)
          if (previousType !== type) {
            pricePerUnit.reset(null);
            lowLimit.reset(null);
            highLimit.reset(null);
            okSupplement.reset(null);
            organizationSupplement.reset(null);
            this.typeResetTrigger.update(value => value + 1);
          }

          if (type === PricePolicyType.FixedWithoutOkMeter) {
            pricePerUnit.setValidators([Validators.required, Validators.max(99.99), Validators.min(0)]);
            okSupplement.enable();
            organizationSupplement.enable();
            lowLimit.clearValidators();
            highLimit.clearValidators();
            fallbackPricePolicy.reset(null);
            fallbackPricePolicy.clearValidators();
          } else if (type === PricePolicyType.FixedWithOkMeter) {
            pricePerUnit.setValidators([Validators.required, Validators.max(99.99), Validators.min(0)]);
            okSupplement.disable();
            organizationSupplement.disable();
            lowLimit.clearValidators();
            highLimit.clearValidators();
            fallbackPricePolicy.reset(null);
            fallbackPricePolicy.clearValidators();
          } else if (type === PricePolicyType.Dynamic) {
            okSupplement.enable();
            organizationSupplement.enable();
            pricePerUnit.clearValidators();
            lowLimit.setValidators([Validators.required, Validators.max(99.99), Validators.min(0)]);
            highLimit.setValidators([Validators.required, Validators.max(99.99), this.lowLimitHighLimitValidator()]);
            fallbackPricePolicy.setValidators(Validators.required);
          } else {
            pricePerUnit.clearValidators();
            lowLimit.clearValidators();
            highLimit.clearValidators();
            fallbackPricePolicy.clearValidators();
          }

          previousType = type;
          pricePerUnit.updateValueAndValidity();
          lowLimit.updateValueAndValidity();
          highLimit.updateValueAndValidity();
          fallbackPricePolicy.updateValueAndValidity();
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    // Revalidate highLimit when lowLimit changes
    this.pricePolicyFormGroup.controls.lowLimit.valueChanges
      .pipe(
        tap(() => this.pricePolicyFormGroup.controls.highLimit.updateValueAndValidity()),
        takeUntilDestroyed()
      )
      .subscribe();

    // Only react to affiliation changes in create mode
    if (!this.editMode) {
      this.pricePolicyFormGroup.controls.affiliation.valueChanges
        .pipe(
          startWith(this.pricePolicyFormGroup.controls.affiliation.value),
          tap(val => {
            if (val) {
              this.pricePolicyFormGroup.controls.organization.clearValidators();
              this.pricePolicyFormGroup.controls.organization.updateValueAndValidity();
              this.pricePolicyFormGroup.controls.organization.disable();
            } else {
              this.pricePolicyFormGroup.controls.organization.setValidators(Validators.required);
              this.pricePolicyFormGroup.controls.organization.updateValueAndValidity();
              this.pricePolicyFormGroup.controls.organization.enable();
            }
          }),
          takeUntilDestroyed()
        )
        .subscribe();
    }
  }

  private lowLimitHighLimitValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const lowLimit = this.pricePolicyFormGroup?.controls.lowLimit.value;
      const highLimit = control.value;
      if (lowLimit != null && highLimit != null && (lowLimit > highLimit || lowLimit === highLimit)) {
        return { lowLimitExceedsHighLimit: true };
      }
      return null;
    };
  }

  public cancel() {
    this.dialogRef.close(this.pricePolicyFormGroup.dirty);
  }

  public resetOrganization() {
    this.pricePolicyFormGroup.patchValue({
      organization: null,
    });
  }

  private handlePricePolicyError(err: { error: { code: number } }) {
    const errorMessage = getPricePolicyErrorMessage(
      this.pricePolicyFormGroup.controls.name.value,
      err,
      this.editMode ? 'update' : 'create'
    );
    this.snackbarService.error(errorMessage);
  }

  public submitCreate(): void {
    this.pricePolicyFormGroup.markAllAsTouched();
    if (this.pricePolicyFormGroup.valid) {
      const noteDialogRef = this.dialog.open(NoteCreationModalComponent, {
        data: {
          title: 'Tilføj note omkring oprettelse',
          confirmText: 'Gem',
          denyText: 'Luk',
        },
      });

      noteDialogRef.afterClosed().subscribe(noteDialogResult => {
        if (!noteDialogResult?.trim?.()) return;
        this.saving.set(true);
        const form = this.pricePolicyFormGroup.getRawValue();
        const request = new CreatePricePolicyRequest(form, noteDialogResult);

        this.pricePolicyService
          .createPricePolicy(request)
          .pipe(finalize(() => this.saving.set(false)))
          .subscribe({
            next: () => {
              this.dialogRef.close(form.name);
            },
            error: err => this.handlePricePolicyError(err),
          });
      });
    }
  }

  public submitUpdate(): void {
    this.pricePolicyFormGroup.markAllAsTouched();
    if (this.pricePolicyFormGroup.valid) {
      const noteDialogRef = this.dialog.open(NoteCreationModalComponent, {
        data: {
          title: 'Tilføj note omkring redigering',
          confirmText: 'Gem',
          denyText: 'Luk',
          icon: { name: 'edit-s' },
        },
      });

      noteDialogRef.afterClosed().subscribe(noteDialogResult => {
        if (!noteDialogResult?.trim?.()) return;
        this.saving.set(true);
        const form = this.pricePolicyFormGroup.getRawValue();
        const updateRequest = new UpdatePricePolicyRequest(this.data.pricePolicy!.id, form, noteDialogResult);

        this.pricePolicyService
          .updatePricePolicy(updateRequest)
          .pipe(finalize(() => this.saving.set(false)))
          .subscribe({
            next: () => {
              this.dialogRef.close(this.data.pricePolicy!.id);
            },
            error: err => this.handlePricePolicyError(err),
          });
      });
    }
  }
}
